<!--
  ColorImageStrip -- the slim "upload your own image for this color" row above
  the Stage preview (Jake request 2026-10-28).

  Always offered for the SELECTED color, whether or not Artifi can render it
  and whatever the Zone diagram / Open canvas switch says. The uploaded image
  LEADS the color's images everywhere that color's image shows (Stage,
  storefront listing, product page, cart); the ThreadFlow render follows it as
  the next image, and Remove makes the render the lead again. The
  server writes it as the color's first storefront_images entry
  (source "upload", card #243), so this component only picks a file, checks it
  and says so; the Stage owns the store calls.

  One row, about 44px tall: a thumbnail of the image shoppers see now, the one
  primary button, Remove (only when there is an upload), and a short hint.
  The accepted types and size sit in the button's tooltip and in the error
  the strip shows for a refused file.
  It replaces the tall tab + panel block the upload used to live in.

  Props in, events out.
-->
<template>
    <div class="color-image-strip" data-testid="stage-color-image-strip">
        <img
            v-if="thumbUrl"
            :src="thumbUrl"
            :alt="customPreviewUrl ? `Your image for ${colorLabel}` : `${colorLabel} preview`"
            class="color-image-strip__thumb"
            data-testid="stage-color-image-thumb"
        />
        <label
            class="color-image-strip__btn color-image-strip__btn--primary"
            :class="{ 'is-disabled': busy }"
            title="PNG, JPEG, WebP or GIF, up to 10 MB"
            data-testid="stage-custom-preview-upload"
        >
            {{ customPreviewUrl ? "Replace image" : "Upload your image" }}
            <input
                type="file"
                class="color-image-strip__file"
                :accept="ACCEPT"
                :disabled="busy"
                data-testid="stage-custom-preview-input"
                @change="onFile"
            />
        </label>
        <button
            v-if="customPreviewUrl"
            type="button"
            class="color-image-strip__btn"
            :disabled="busy"
            data-testid="stage-custom-preview-remove"
            @click="$emit('remove')"
        >
            Remove
        </button>
        <p class="color-image-strip__hint" data-testid="stage-color-image-hint">
            <template v-if="customPreviewUrl">
                Shoppers see your image first for {{ colorLabel }}.
            </template>
            <template v-else>
                Shown first for {{ colorLabel }}, ahead of the preview.
            </template>
        </p>
        <p
            v-if="busy"
            class="color-image-strip__note"
            data-testid="stage-custom-preview-status"
            role="status"
        >
            Saving the preview image...
        </p>
        <p
            v-if="errorMessage"
            class="color-image-strip__note color-image-strip__note--error"
            data-testid="stage-custom-preview-error"
            role="alert"
        >
            {{ errorMessage }}
        </p>
    </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";

const props = defineProps({
    colorLabel: { type: String, required: true },
    // The color's supplier photo: the thumbnail until an image is uploaded.
    blankUrl: { type: String, default: null },
    // The color's uploaded preview image (storefront_images, source upload).
    customPreviewUrl: { type: String, default: null },
    // {state: idle|uploading|error, message}
    status: { type: Object, default: () => ({ state: "idle", message: null }) },
});
const emit = defineEmits(["upload", "remove"]);

// Mirrors the server's accepted types (magic-byte sniffed there) and cap.
const ACCEPT = "image/png,image/jpeg,image/webp,image/gif";
const ACCEPTED_TYPES = new Set(ACCEPT.split(","));
const MAX_BYTES = 10 * 1024 * 1024;

const localError = ref(null);
watch(
    () => props.colorLabel,
    () => {
        localError.value = null;
    },
);

const thumbUrl = computed(() => props.customPreviewUrl || props.blankUrl || null);
const busy = computed(() => props.status?.state === "uploading");
const errorMessage = computed(
    () =>
        localError.value ||
        (props.status?.state === "error" ? props.status.message : null),
);

function onFile(ev) {
    const input = ev.target;
    const file = input?.files?.[0];
    localError.value = null;
    if (!file) return;
    if (!ACCEPTED_TYPES.has(file.type)) {
        localError.value = "Upload a PNG, JPEG, WebP or GIF image.";
    } else if (file.size > MAX_BYTES) {
        localError.value = "The image is larger than 10 MB.";
    } else {
        emit("upload", file);
    }
    // Let the same file be picked again after a failure.
    try {
        input.value = "";
    } catch {
        // Some test doubles make `value` read-only; nothing to reset.
    }
}
</script>

<style scoped>
.color-image-strip {
    flex: 0 0 auto;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 10px;
    padding: 6px 12px;
    border-bottom: 1px solid var(--tf-border, #e0e0e0);
    background: var(--tf-surface, #fff);
    font-size: 12px;
    color: var(--tf-text-muted, #6b7280);
}
.color-image-strip__thumb {
    flex: 0 0 auto;
    width: 32px;
    height: 32px;
    object-fit: contain;
    border: 1px solid var(--tf-border, #d1d5db);
    border-radius: 4px;
    background: #fff;
}
.color-image-strip__btn {
    position: relative;
    flex: 0 0 auto;
    padding: 5px 12px;
    border: 1px solid var(--tf-border, #d1d5db);
    border-radius: 4px;
    background: #fff;
    font-size: 13px;
    cursor: pointer;
}
.color-image-strip__btn--primary {
    background: var(--tf-primary, #1f2937);
    border-color: var(--tf-primary, #1f2937);
    color: #fff;
}
.color-image-strip__btn.is-disabled,
.color-image-strip__btn:disabled {
    opacity: 0.5;
    cursor: default;
}
/* Visually hidden, still focusable through its label. */
.color-image-strip__file {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    overflow: hidden;
}
.color-image-strip__hint {
    /* Wraps INSIDE the row (basis 0), so the strip stays one slim row. */
    flex: 1 1 0;
    min-width: 110px;
    margin: 0;
    line-height: 1.3;
}
.color-image-strip__note {
    flex: 1 0 100%;
    margin: 0;
}
.color-image-strip__note--error {
    color: var(--tf-text-error, #b91c1c);
}
</style>
